// src/integrations/supabase/functions.ts
import { supabase, supabaseAnonKey } from '@/integrations/supabase/client';

type ResponseMode = 'json' | 'arrayBuffer' | 'text';

type CallFnOptions = {
  body?: unknown;
  headers?: Record<string, string>;
  as?: ResponseMode; // default 'json'
  method?: 'POST' | 'GET';
};

type CallFnResult<T> = {
  data: T | null;
  error?: { message: string; status?: number };
  status: number;
};

const PROJECT_ID = 'daeuiiggqdxyseiekbca';
const SUPABASE_URL = `https://${PROJECT_ID}.supabase.co`;

async function buildAuthHeader(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  const anon = supabaseAnonKey;
  return `Bearer ${token ?? anon}`;
}

function defaultHeaders(): Record<string, string> {
  // Detect platform for better debugging
  const getPlatform = () => {
    if (typeof window === 'undefined') return 'server';
    const userAgent = window.navigator?.userAgent || '';
    if (userAgent.includes('PrayAI/iOS')) return 'ios';
    if (userAgent.includes('PrayAI/Android')) return 'android';
    if (window.location?.protocol === 'capacitor:') return 'capacitor';
    return 'webapp';
  };

  return {
    'Content-Type': 'application/json',
    'x-client-info': 'supabase-js-web',
    'x-prayai-client': getPlatform(),
  };
}

/**
 * Call Supabase Edge Functions via the correct subdomain.
 * Uses https://<project>.functions.supabase.co/<name>
 */
export async function callFunction<T = any>(
  name: string,
  opts: CallFnOptions = {}
): Promise<CallFnResult<T>> {
  const url = `https://${PROJECT_ID}.functions.supabase.co/${encodeURIComponent(name)}`;

  const method = opts.method ?? 'POST';
  const apikey = supabaseAnonKey;
  const auth = await buildAuthHeader();

  const headers: Record<string, string> = {
    ...defaultHeaders(),
    apikey,
    Authorization: auth,
    ...(opts.headers ?? {}),
  };

  // For non-JSON responses we must use fetch directly.
  const mode: ResponseMode = opts.as ?? 'json';

  try {
    const res = await fetch(url, {
      method,
      headers,
      body: method === 'POST' ? JSON.stringify(opts.body ?? {}) : undefined,
    });

    const status = res.status;

    if (!res.ok) {
      // Try to surface any text body for diagnostics
      let msg = `HTTP ${status}`;
      try {
        const t = await res.text();
        if (t) msg = t.slice(0, 400);
      } catch {}
      console.error(`[callFunction:${name}] HTTP ${status}:`, msg);
      return { data: null, error: { message: msg, status }, status };
    }

    if (mode === 'arrayBuffer') {
      const buf = await res.arrayBuffer();
      return { data: buf as unknown as T, status };
    }
    if (mode === 'text') {
      const text = await res.text();
      return { data: text as unknown as T, status };
    }

    // Default JSON
    const json = await res.json().catch(async () => {
      const t = await res.text();
      throw new Error(t || 'Non-JSON response');
    });
    return { data: json as T, status };
  } catch (err: any) {
    // Network/TLS/SSL error ends up here
    const message =
      err?.message?.includes('SSL') || err?.message?.includes('cipher')
        ? 'Network/TLS error reaching Edge Function.'
        : err?.message || 'Failed to fetch';
    console.error(`[callFunction:${name}] Network error:`, err);
    return { data: null, error: { message }, status: 0 };
  }
}

export default { callFunction };